import {cn} from '@tempmee/core'
import {Column, Typography} from '@tempmee/react'
import {OutlineClipboardListIcon} from '@tempmee/react-icons'
import type {ReactNode} from 'react'

export interface EmptyStateProps {
  /** Glyph shown above the message. Defaults to a clipboard-list icon. */
  icon?: ReactNode
  /** Primary message — e.g. "No activity yet". */
  title: ReactNode
  /** Optional supporting copy beneath the title. */
  description?: ReactNode
  /** Optional action beneath the copy (e.g. a `Button`). */
  action?: ReactNode
  className?: string
}

/**
 * Internal: the centered empty placeholder shown by the collection-rendering
 * section blocks (`ChartCard`, `TableCard`, `ActivityList`) when they have no
 * content. Not a public export — kept block-internal for v1 rather than promoted
 * to a `@tempmee/react` primitive.
 */
export function EmptyState({icon, title, description, action, className}: EmptyStateProps) {
  return (
    <Column
      align="center"
      justify="center"
      gap={2}
      className={cn('px-4 py-10 text-center', className)}
    >
      {/* Icon-only wrapper — structural, no text (see reusing-components). */}
      <span className="text-neutral-200" aria-hidden>
        {icon ?? <OutlineClipboardListIcon size={40} />}
      </span>
      <Column align="center" gap={1}>
        <Typography variant="subtitle2" as="p" className="text-text-primary">
          {title}
        </Typography>
        {description != null && (
          <Typography variant="body2" as="p" className="text-text-secondary">
            {description}
          </Typography>
        )}
      </Column>
      {action}
    </Column>
  )
}
